import React from "react";
import { Info } from "lucide-react";
import { Select, Input, Checkbox } from "antd";
import Tooltip from "../Tooltip";
import SearchableLookupField from "./SearchableLookupField";
import {
  searchProjectsByName,
  searchProjectObjectivesByName,
  searchProjectPagesByName,
  searchProjectQualStepsByName,
  searchQualStepsByName,
  searchAccountByName,
  searchContributorByName,
} from "../../services/salesforceApi";
const { TextArea } = Input;
/**
 * EditableField Component
 * Renders a single form field based on type
 */
const EditableField = ({
  label,
  path,
  type = "text",
  value,
  onChange,
  options = [],
  readOnly = false,
  required = false,
  placeholder = "",
  info = "",
  showError = false,
  searchType = null,
  displayName,
  actions,
  searchParams = {},
}) => {
  const isInvalid =
    showError && required && (!value || String(value).trim() === "");

  const labelElement = (
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      <span className="flex items-center gap-2">
        <span>
          {label} {required && <span className="text-red-500">*</span>}
        </span>
        {info && (
          <Tooltip content={info}>
            <Info
              size={14}
              className="text-blue-500 hover:text-gray-400 flex-shrink-0"
            />
          </Tooltip>
        )}
      </span>
    </label>
  );

  let control = null;

  // Handle searchable fields
  if (type === "searchable") {
    let searchFunction = null;
    if (searchType === "contributor") {
      searchFunction = searchContributorByName;
    } else if (searchType === "account") {
      searchFunction = searchAccountByName;
    } else if (searchType === "project") {
      searchFunction = searchProjectsByName;
    } else if (searchType === "projectObjective") {
      searchFunction = searchProjectObjectivesByName;
    } else if (searchType === "projectPage") {
      searchFunction = searchProjectPagesByName;
    } else if (searchType === "projectQualStep") {
      searchFunction = searchProjectQualStepsByName;
    } else if (searchType === "qualificationStep") {
      searchFunction = searchQualStepsByName;
    }
    return (
      <div>
        <SearchableLookupField
          label={label}
          value={value ?? ""}
          displayValue={displayName}
          onChange={(displayValue, id) => {
            onChange(path, id ?? displayValue, displayValue);
          }}
          searchFunction={searchFunction}
          searchParams={searchParams}
          placeholder={placeholder || `Search ${searchType}...`}
          required={required}
          showError={showError}
          minChars={3}
        />
        {isInvalid && (
          <p className="text-xs text-red-600 mt-1">This field is required</p>
        )}
      </div>
    );
  }

  if (readOnly) {
    control = (
      <input
        type="text"
        value={value ?? ""}
        readOnly
        disabled
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
      />
    );
  } else if (type === "select") {
    control = (
      <select
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
        className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          isInvalid
            ? "border-red-500 bg-red-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        required={required}
      >
        <option value="">--None--</option>
        {options.map((opt) => (
          <option
            key={typeof opt === "string" ? opt : opt.value}
            value={typeof opt === "string" ? opt : opt.value}
          >
            {typeof opt === "string" ? opt : opt.label}
          </option>
        ))}
      </select>
    );
  } else if (type === "multi-select") {
    const arrayValue = Array.isArray(value)
      ? value
      : typeof value === "string" && value
        ? value.split(";")
        : [];
    control = (
      <Select
        mode="multiple"
        allowClear
        className="w-full rounded text-gray-900"
        value={arrayValue}
        placeholder="Select Options"
        onChange={(selectedValues) => onChange(path, selectedValues)}
      >
        {options.map((opt) => {
          // Options must be objects with label and value properties
          return (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          );
        })}
      </Select>
    );
  } else if (type === "checkbox") {
    control = (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <Checkbox
          checked={Boolean(value)}
          className="pt-5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer top-margin"
          onChange={(e) => onChange(path, e.target.checked)}
        />
      </label>
    );
  } else if (type === "textarea") {
    control = (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
        placeholder={placeholder}
        rows={3}
        className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
          isInvalid
            ? "border-red-500 bg-red-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        required={required}
      />
    );
  } else if (type === "date") {
    control = (
      <input
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
        className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          isInvalid
            ? "border-red-500 bg-red-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        required={required}
      />
    );
  } else if (type === "number") {
    control = (
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          isInvalid
            ? "border-red-500 bg-red-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        required={required}
      />
    );
  } else if (type === "file") {
    const fileDisplay = formatFileFieldDisplay(value);
    control = (
      <div className="relative min-h-[80px]">
        <TextArea
          className="w-full rounded text-gray-900 pr-24"
          value={fileDisplay}
          placeholder="S3 key or file path..."
          onChange={(e) => onChange(path, e.target.value)}
          rows={3}
        />
        {actions && (
          <div
            className="absolute top-2 right-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
      </div>
    );
  } else {
    // text
    control = (
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          isInvalid
            ? "border-red-500 bg-red-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        required={required}
      />
    );
  }

  return (
    <div>
      {labelElement}
      {control}
      {isInvalid && (
        <p className="text-xs text-red-600 mt-1">This field is required</p>
      )}
    </div>
  );
};

export function formatFileFieldDisplay(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const pathname = new URL(s).pathname;
      return pathname.slice(1) || s.slice(0, 60);
    } catch {
      return s.length > 60 ? s.slice(0, 60) + "…" : s;
    }
  }
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
}
/**
 * Helper to get nested property value
 */

export function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length; i += 1) {
    const key = parts[i];
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[key];
  }
  return cur;
}

/**
 * renderGrid - Renders a grid of form fields
 *
 * @param {Array} fields - Array of field definitions
 * @param {Object} options - Rendering options
 * @returns JSX
 *
 * Field definition:
 * {
 *   label: string,
 *   path: string (e.g., "metadata.name"),
 *   type: "text" | "select" | "checkbox" | "textarea" | "date" | "number",
 *   options: Array (for select),
 *   required: boolean,
 *   readOnly: boolean,
 *   placeholder: string,
 *   info: string (tooltip text),
 *   showError: boolean (show validation error),
 *   cols: number (1, 2, or 3 - how many columns this field spans)
 * }
 */
export function renderGrid(
  fields,
  { formData, onChange, showValidation = false, searchParams } = {},
) {
  return (
    <div className="space-y-4">
      {fields.map((f, idx) => {
        const key = f.path || f.label || idx;
        const val = getByPath(formData, f.path);
        const cols = f.cols || 1; // default to 1 column

        // Determine grid class based on cols
        let gridClass = "grid gap-4";
        if (cols === 1) {
          gridClass = ""; // full width, no grid
        } else if (cols === 2) {
          gridClass = "grid grid-cols-1 md:grid-cols-2 gap-4";
        } else if (cols === 3) {
          gridClass = "grid grid-cols-1 md:grid-cols-3 gap-4";
        }

        // If this is a section (no path), render as a wrapper
        if (!f.path && f.fields) {
          return (
            <div key={key} className={gridClass}>
              {f.fields.map((subField, subIdx) => {
                const subKey =
                  subField.path || subField.label || `${key}-${subIdx}`;
                const subVal = getByPath(formData, subField.path);
                const subDisplayName =
                  subField.type === "searchable"
                    ? getByPath(formData, `${subField.path}DisplayName`)
                    : undefined;
                return (
                  <EditableField
                    key={subKey}
                    label={subField.label}
                    path={subField.path}
                    type={subField.type}
                    searchType={subField.searchType}
                    searchParams={searchParams}
                    value={subVal}
                    options={subField.options}
                    readOnly={subField.readOnly}
                    required={subField.required}
                    placeholder={subField.placeholder}
                    info={subField.info}
                    showError={showValidation && subField.required}
                    onChange={onChange}
                    displayName={subDisplayName}
                  />
                );
              })}
            </div>
          );
        }

        // Regular field
        return (
          <div key={key} className={cols > 1 ? gridClass : ""}>
            <EditableField
              label={f.label}
              path={f.path}
              type={f.type}
              searchType={f.searchType}
              value={val}
              options={f.options}
              readOnly={f.readOnly}
              required={f.required}
              placeholder={f.placeholder}
              info={f.info}
              showError={showValidation && f.required}
              onChange={onChange}
              displayName={
                f.type === "searchable"
                  ? getByPath(formData, `${f.path}DisplayName`)
                  : undefined
              }
              searchParams={searchParams}
            />
          </div>
        );
      })}
    </div>
  );
}

export default renderGrid;
